import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import { useState } from "react"

const plans = [
  {
    name: "Standard",
    price: "$97",
    priceId: "price_1QkXIbEiWhAkWDnriNJJHYG3",
    features: [
      "Unlimited messages",
      "Priority support",
      "Advanced analytics",
      "Custom knowledge base",
    ],
  },
  {
    name: "Pro",
    price: "$297",
    priceId: "price_1QkXIbEiWhAkWDnrwNOyeoG4",
    features: [
      "Everything in Standard",
      "Premium support",
      "Advanced customization",
      "API access",
      "Custom integrations",
    ],
  },
  {
    name: "Agency",
    price: "$997",
    priceId: "price_1QkXIbEiWhAkWDnr9iKgXLTj",
    features: [
      "Everything in Pro",
      "Dedicated account manager",
      "Custom development",
      "White-label solution",
      "Priority feature requests",
      "24/7 support",
    ],
  }
]

export default function Pricing() {
  const { session } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [couponCode, setCouponCode] = useState("")

  const handleSubscribe = async (priceId: string) => {
    if (!session) {
      navigate("/login")
      return
    }

    try {
      console.log('Creating checkout session...')
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          priceId,
          couponCode: couponCode.trim() || undefined, // Only send if not empty
          successUrl: `${window.location.origin}/dashboard`,
          cancelUrl: `${window.location.origin}/pricing`,
        }
      })

      if (error) {
        // Parse the error message from the response body if it exists
        let errorMessage = error.message
        try {
          const errorBody = JSON.parse(error.body)
          if (errorBody.error) {
            errorMessage = errorBody.error
          }
        } catch (e) {
          // If parsing fails, use the original error message
          console.error("Error parsing error body:", e)
        }

        toast({
          variant: "destructive",
          title: "Subscription Error",
          description: errorMessage,
        })
        return
      }

      if (data?.url) {
        console.log('Redirecting to checkout...')
        window.location.href = data.url
      } else {
        throw new Error('No checkout URL received')
      }
    } catch (error) {
      console.error("Error creating checkout session:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create checkout session. Please try again.",
      })
    }
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-muted-foreground">
          Select the perfect plan for your needs
        </p>
        <div className="mt-6 max-w-sm mx-auto">
          <Input
            type="text"
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            className="mb-4"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {plans.map((plan) => (
          <Card key={plan.name} className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold">{plan.name}</h2>
              <p className="text-3xl font-bold mt-2">{plan.price}</p>
              <p className="text-muted-foreground">per month</p>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center">
                  <svg
                    className="w-5 h-5 text-green-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              className="w-full"
              onClick={() => handleSubscribe(plan.priceId)}
            >
              Subscribe Now
            </Button>
          </Card>
        ))}
      </div>
    </div>
  )
}